const SCROLLPORT_OVERFLOW = new Set(["auto", "scroll", "overlay"]);

/** True when the element is a vertical scroll container, not merely clipped. */
export function isQuestionnaireScrollport(element: HTMLElement): boolean {
	const overflowY = getComputedStyle(element).overflowY;
	return SCROLLPORT_OVERFLOW.has(overflowY);
}

/**
 * The dialog panel is its own scroller. The standalone page panel is
 * overflow-visible, so the document (or a real ancestor scroller) moves.
 * Writing scrollTop on the visible panel is a no-op — the missing auto-scroll.
 */
export function resolveQuestionnaireScrollport(panel: HTMLElement): HTMLElement {
	if (isQuestionnaireScrollport(panel)) return panel;

	let node = panel.parentElement;
	while (node) {
		if (isQuestionnaireScrollport(node) && node.scrollHeight > node.clientHeight + 1) {
			return node;
		}
		node = node.parentElement;
	}

	const scrolling = panel.ownerDocument.scrollingElement;
	if (scrolling instanceof HTMLElement) return scrolling;
	return panel.ownerDocument.documentElement;
}

function visibleBottom(port: HTMLElement): number {
	const doc = port.ownerDocument;
	const view = doc.defaultView;
	if (port === doc.scrollingElement || port === doc.documentElement || port === doc.body) {
		return view?.innerHeight ?? port.clientHeight;
	}
	return port.getBoundingClientRect().bottom;
}

/** Scroll offset that brings the bottom of the transcript into the scrollport. Downward only. */
export function questionnaireRevealScrollTop(port: HTMLElement, panel: HTMLElement): number {
	const max = Math.max(0, port.scrollHeight - port.clientHeight);
	if (port === panel) return max;

	const delta = panel.getBoundingClientRect().bottom - visibleBottom(port);
	if (delta <= 1) return port.scrollTop;
	return Math.min(max, Math.max(0, port.scrollTop + delta));
}

/**
 * Pointer activation focuses the control, and the browser then scrolls that
 * control into view on its own. That stacks on the transcript glide.
 * Keyboard focus is left alone.
 */
export function preventQuestionnairePointerScroll(event: { preventDefault: () => void; pointerType: string }): void {
	if (event.pointerType === "mouse" || event.pointerType === "pen" || event.pointerType === "touch") {
		event.preventDefault();
	}
}
